/**
 * LoanTermsSection - Loan terms validation section
 */

import { Calculator, DollarSign, TrendingUp } from 'lucide-react';
import { useValidationStore } from '../../store';
import { ValidationFieldRow } from './ValidationFieldRow';
import { ValidationSection } from './ValidationSection';

export function LoanTermsSection({
  extractedData,
}: {
  extractedData: {
    loanAmount?: number;
    interestRate?: number;
    loanTerm?: number;
    monthlyPrincipalAndInterest?: number;
    loanType?: string;
    loanPurpose?: string;
    loanProduct?: string;
    prepaymentPenalty?: boolean;
    prepaymentPenaltyDetails?: string;
    balloonPayment?: boolean;
    balloonPaymentAmount?: number;
    balloonPaymentDueDate?: string;
    projectedPayments?: Array<{
      years: number[];
      principalAndInterest?: number;
      mortgageInsurance?: number;
      estimatedEscrow?: number;
      estimatedTotal?: number;
    }>;
    hasEscrow?: boolean;
    escrowedItems?: string[];
    estimatedEscrowPayment?: number;
    estimatedTaxesInsuranceAssessments?: number;
    totalPayments?: number;
    financeCharge?: number;
    apr?: number;
    totalInterestPercentage?: number;
    cashToClose?: number;
  };
}) {
  const { expandedSections, toggleSection } = useValidationStore();
  const isExpanded = expandedSections.has('loanTerms');
  const isPaymentsExpanded = expandedSections.has('payments');
  const isCalculationsExpanded = expandedSections.has('calculations');

  const hasLoanTerms =
    extractedData.loanAmount ||
    extractedData.interestRate ||
    extractedData.loanTerm ||
    extractedData.loanType ||
    extractedData.loanPurpose ||
    extractedData.loanProduct ||
    extractedData.prepaymentPenalty !== undefined ||
    extractedData.balloonPayment !== undefined;

  const hasPayments =
    extractedData.monthlyPrincipalAndInterest ||
    extractedData.projectedPayments?.length ||
    extractedData.estimatedEscrowPayment ||
    extractedData.estimatedTaxesInsuranceAssessments ||
    extractedData.escrowedItems?.length;

  const hasCalculations =
    extractedData.totalPayments ||
    extractedData.financeCharge ||
    extractedData.apr ||
    extractedData.totalInterestPercentage ||
    extractedData.cashToClose;

  if (!hasLoanTerms && !hasPayments && !hasCalculations) {
    return null;
  }

  return (
    <>
      {/* Loan Terms Section */}
      {hasLoanTerms && (
        <ValidationSection
          title="Loan Terms"
          description="Loan amount, interest rate, and product details"
          icon={<Calculator className="h-5 w-5 text-muted-foreground" />}
          isExpanded={isExpanded}
          onToggle={() => toggleSection('loanTerms')}
        >
          <div className="space-y-3">
            {extractedData.loanAmount && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="loanAmount"
                label="Loan Amount"
                type="currency"
                originalValue={extractedData.loanAmount}
              />
            )}

            {extractedData.interestRate !== undefined && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="interestRate"
                label="Interest Rate"
                type="percentage"
                originalValue={extractedData.interestRate}
              />
            )}

            {extractedData.loanTerm && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="loanTerm"
                label="Loan Term"
                type="number"
                originalValue={extractedData.loanTerm}
                suffix=" months"
              />
            )}

            {extractedData.loanType && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="loanType"
                label="Loan Type"
                type="text"
                originalValue={extractedData.loanType}
              />
            )}

            {extractedData.loanPurpose && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="loanPurpose"
                label="Loan Purpose"
                type="text"
                originalValue={extractedData.loanPurpose}
              />
            )}

            {extractedData.loanProduct && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="loanProduct"
                label="Loan Product"
                type="text"
                originalValue={extractedData.loanProduct}
              />
            )}

            {extractedData.prepaymentPenalty !== undefined && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="prepaymentPenalty"
                label="Prepayment Penalty"
                type="boolean"
                originalValue={extractedData.prepaymentPenalty}
              />
            )}

            {extractedData.prepaymentPenaltyDetails && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="prepaymentPenaltyDetails"
                label="Prepayment Penalty Details"
                type="text"
                originalValue={extractedData.prepaymentPenaltyDetails}
              />
            )}

            {extractedData.balloonPayment !== undefined && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="balloonPayment"
                label="Balloon Payment"
                type="boolean"
                originalValue={extractedData.balloonPayment}
              />
            )}

            {extractedData.balloonPaymentAmount && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="balloonPaymentAmount"
                label="Balloon Payment Amount"
                type="currency"
                originalValue={extractedData.balloonPaymentAmount}
              />
            )}

            {extractedData.balloonPaymentDueDate && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="balloonPaymentDueDate"
                label="Balloon Payment Due Date"
                type="date"
                originalValue={extractedData.balloonPaymentDueDate}
              />
            )}
          </div>
        </ValidationSection>
      )}

      {/* Projected Payments Section */}
      {hasPayments && (
        <ValidationSection
          title="Projected Payments & Escrow"
          description="Monthly payment breakdown and escrow details"
          icon={<DollarSign className="h-5 w-5 text-muted-foreground" />}
          isExpanded={isPaymentsExpanded}
          onToggle={() => toggleSection('payments')}
        >
          <div className="space-y-3">
            {extractedData.monthlyPrincipalAndInterest && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="monthlyPrincipalAndInterest"
                label="Monthly Principal & Interest"
                type="currency"
                originalValue={extractedData.monthlyPrincipalAndInterest}
              />
            )}

            {extractedData.projectedPayments?.map((payment, index) => (
              <div key={index} className="p-3 bg-muted/30 rounded-lg">
                <div className="text-sm font-medium text-muted-foreground mb-2">
                  Years {payment.years.join(' - ')}
                </div>
                {payment.principalAndInterest && (
                  <ValidationFieldRow
                    category="loanTerms"
                    fieldKey={`projectedPayment_${index}_pni`}
                    label="Principal & Interest"
                    type="currency"
                    originalValue={payment.principalAndInterest}
                  />
                )}
                {payment.mortgageInsurance && (
                  <ValidationFieldRow
                    category="loanTerms"
                    fieldKey={`projectedPayment_${index}_mi`}
                    label="Mortgage Insurance"
                    type="currency"
                    originalValue={payment.mortgageInsurance}
                  />
                )}
                {payment.estimatedEscrow && (
                  <ValidationFieldRow
                    category="loanTerms"
                    fieldKey={`projectedPayment_${index}_escrow`}
                    label="Estimated Escrow"
                    type="currency"
                    originalValue={payment.estimatedEscrow}
                  />
                )}
                {payment.estimatedTotal && (
                  <ValidationFieldRow
                    category="loanTerms"
                    fieldKey={`projectedPayment_${index}_total`}
                    label="Estimated Total"
                    type="currency"
                    originalValue={payment.estimatedTotal}
                  />
                )}
              </div>
            ))}

            {extractedData.estimatedTaxesInsuranceAssessments && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="estimatedTaxesInsuranceAssessments"
                label="Est. Taxes, Insurance & Assessments"
                type="currency"
                originalValue={extractedData.estimatedTaxesInsuranceAssessments}
              />
            )}

            {extractedData.estimatedEscrowPayment && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="estimatedEscrowPayment"
                label="Monthly Escrow Payment"
                type="currency"
                originalValue={extractedData.estimatedEscrowPayment}
              />
            )}

            {extractedData.hasEscrow !== undefined && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="hasEscrow"
                label="Has Escrow Account"
                type="boolean"
                originalValue={extractedData.hasEscrow}
              />
            )}

            {extractedData.escrowedItems && extractedData.escrowedItems.length > 0 && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="escrowedItems"
                label="Escrowed Items"
                type="text"
                originalValue={extractedData.escrowedItems.join(', ')}
              />
            )}
          </div>
        </ValidationSection>
      )}

      {/* Loan Calculations Section */}
      {hasCalculations && (
        <ValidationSection
          title="Loan Calculations"
          description="Finance charges, APR, and closing costs"
          icon={<TrendingUp className="h-5 w-5 text-muted-foreground" />}
          isExpanded={isCalculationsExpanded}
          onToggle={() => toggleSection('calculations')}
        >
          <div className="space-y-3">
            {extractedData.totalPayments && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="totalPayments"
                label="Total of Payments"
                type="currency"
                originalValue={extractedData.totalPayments}
              />
            )}

            {extractedData.financeCharge && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="financeCharge"
                label="Finance Charge"
                type="currency"
                originalValue={extractedData.financeCharge}
              />
            )}

            {extractedData.apr && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="apr"
                label="Annual Percentage Rate (APR)"
                type="percentage"
                originalValue={extractedData.apr}
              />
            )}

            {extractedData.totalInterestPercentage && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="totalInterestPercentage"
                label="Total Interest Percentage (TIP)"
                type="percentage"
                originalValue={extractedData.totalInterestPercentage}
              />
            )}

            {extractedData.cashToClose && (
              <ValidationFieldRow
                category="loanTerms"
                fieldKey="cashToClose"
                label="Cash to Close"
                type="currency"
                originalValue={extractedData.cashToClose}
              />
            )}
          </div>
        </ValidationSection>
      )}
    </>
  );
}
